import type { BetaFeature } from 'definitions/beta-features';
import type { BetaFeatureCompany, BetaFeatureCompanySummary, BetaFeatureListing } from 'types';
import { useState } from 'react';
import { PERTENTO_COMPANY_ID } from 'definitions';
import { PERTENTO_BETA_REFUSAL } from 'definitions/beta-features';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useDebounce } from 'hooks/useDebounce';
import { readApiError } from './billing/api-error';

const LIST_KEY = ['ADMIN_BETA_FEATURES'] as const;
const SEARCH_KEY = ['ADMIN_BETA_FEATURES', 'company-search'] as const;

/** How long the company search waits after the last keystroke before asking the server. */
const SEARCH_DEBOUNCE_MS = 250;

export type PendingRemoval = { listing: BetaFeatureListing; company: BetaFeatureCompany };

export type PendingEnable = { listing: BetaFeatureListing; company: BetaFeatureCompanySummary };

type EnableParams = { feature: BetaFeature; companyId: number };

const toastFailure = (title: string) => (error: unknown) =>
  toast.error(title, { description: readApiError(error).message ?? 'Please try again in a moment.' });

/** "Acme, client of Agency X" or "Agency X, agency with 3 clients": who a switch reaches. */
export const companyReach = (company: BetaFeatureCompanySummary): string | null => {
  if (company.agency) return `Client of ${company.agency.name}`;
  if (company.type !== 'Agency') return null;
  if (company.clientCount === 0) return 'Agency, no clients yet';
  return `Agency, also on for its ${company.clientCount} ${company.clientCount === 1 ? 'client' : 'clients'}`;
};

/**
 * What switching a feature off for this company does beyond the company itself: an agency's
 * clients lose it with it (unless switched on themselves), and a client whose agency has it keeps
 * it. Null when it reaches no one else.
 */
export const removalConsequence = ({ listing, company }: PendingRemoval): string | null => {
  const label = listing.label;
  const agency = company.agency;
  if (agency && listing.companies.some((other) => other.id === agency.id)) {
    return `${company.name} keeps ${label} while its agency, ${agency.name}, has it on.`;
  }
  if (company.clientCount > 0) {
    const clients = company.clientCount === 1 ? 'Its client loses' : `Its ${company.clientCount} clients lose`;
    return `${clients} ${label} too, except any switched on for themselves.`;
  }
  return null;
};

/**
 * Why a company cannot be picked to switch a feature on, or null when it can. Pertento's own
 * company is refused by the API too (PERTENTO_BETA_REFUSAL).
 */
export const enableBlockedReason = (company: Pick<BetaFeatureCompanySummary, 'id'>): string | null =>
  company.id === PERTENTO_COMPANY_ID ? PERTENTO_BETA_REFUSAL : null;

/**
 * What switching a feature on for this company does beyond the company itself, or null when it
 * reaches no one else and needs no confirm: an agency's switch reaches every client it manages.
 */
export const enableConsequence = (company: Pick<BetaFeatureCompanySummary, 'clientCount'>): string | null => {
  if (company.clientCount === 0) return null;
  const clients = company.clientCount === 1 ? '1 client company' : `${company.clientCount} client companies`;
  return `Also switches it on for ${clients}, and for every client it adds later.`;
};

/**
 * Admin, Beta features, as one manager: the list of features with their companies, the company
 * search behind the add control, and the confirms: before switching a feature on for a company
 * whose switch reaches its clients, and before switching it off.
 */
export const useAdminBetaFeatures = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const listQuery = useQuery<BetaFeatureListing[]>({
    queryKey: LIST_KEY,
    queryFn: () => client.get('/admin/beta-features'),
  });

  // One search for the screen: only one add control is open at a time.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useDebounce(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS, [search]);

  const searchQuery = useQuery<BetaFeatureCompanySummary[]>({
    queryKey: [...SEARCH_KEY, debouncedSearch],
    queryFn: () => client.get(`/admin/beta-features/companies?search=${encodeURIComponent(debouncedSearch)}`),
    enabled: debouncedSearch.length > 0,
  });

  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [pendingEnable, setPendingEnable] = useState<PendingEnable | null>(null);

  const enableMutation = useMutation({
    mutationFn: ({ feature, companyId }: EnableParams) =>
      client.post(`/admin/beta-features/${feature}/companies`, { companyId }),
    onSuccess: () => toast.success('Beta feature switched on.'),
    onError: toastFailure('Could not switch the beta feature on'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });

  const disableMutation = useMutation({
    mutationFn: ({ feature, companyId }: EnableParams) =>
      client.delete(`/admin/beta-features/${feature}/companies/${companyId}`),
    onSuccess: () => toast.success('Beta feature switched off.'),
    onError: toastFailure('Could not switch the beta feature off'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });

  const enable = async ({ feature, companyId }: EnableParams) => {
    setSearch('');
    await enableMutation.mutateAsync({ feature, companyId }).catch(() => undefined);
  };

  /** A company picked to switch a feature on for: straight on, or a confirm when it reaches clients. */
  const chooseCompany = ({ listing, company }: PendingEnable) => {
    if (enableBlockedReason(company)) return;
    if (enableConsequence(company)) {
      setPendingEnable({ listing, company });
      return;
    }
    enable({ feature: listing.feature, companyId: company.id });
  };

  const confirmEnable = async () => {
    if (!pendingEnable) return;
    try {
      await enableMutation.mutateAsync({ feature: pendingEnable.listing.feature, companyId: pendingEnable.company.id });
      setPendingEnable(null);
      setSearch('');
    } catch {
      // Toasted by the mutation; the dialog stays open to retry or cancel.
    }
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    try {
      await disableMutation.mutateAsync({
        feature: pendingRemoval.listing.feature,
        companyId: pendingRemoval.company.id,
      });
      setPendingRemoval(null);
    } catch {
      // Toasted by the mutation; the dialog stays open to retry or cancel.
    }
  };

  const isSearching = search.trim().length > 0 && (search.trim() !== debouncedSearch || searchQuery.isFetching);

  return {
    list: {
      data: listQuery.data,
      isLoading: !listQuery.data && !listQuery.isError,
      isError: listQuery.isError,
      retry: () => listQuery.refetch(),
    },
    search,
    setSearch,
    searchResults: debouncedSearch ? (searchQuery.data ?? []) : [],
    isSearching,
    chooseCompany,
    isEnabling: enableMutation.isPending,
    pendingEnable,
    pendingEnableConsequence: pendingEnable ? enableConsequence(pendingEnable.company) : null,
    confirmEnable,
    cancelEnable: () => setPendingEnable(null),
    pendingRemoval,
    pendingRemovalConsequence: pendingRemoval ? removalConsequence(pendingRemoval) : null,
    requestRemoval: setPendingRemoval,
    cancelRemoval: () => setPendingRemoval(null),
    confirmRemoval,
  };
};

export type AdminBetaFeaturesManager = ReturnType<typeof useAdminBetaFeatures>;
